import { useEffect, useRef, useState } from 'react';
import { ServiceType, type IWorkerSettingsInput } from '../../../__generated__/graphql';
import {
  useMyWorkerQuery,
  useUpdateWorkerMutation,
  useUpsertServiceOfferingMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import {
  offeringForms,
  SERVICE_TYPES,
  toOfferingInput,
  type OfferingForm,
  type PriceErrors,
} from './priceTables';
import { fieldProblemsOf, serviceErrorMessage } from './serviceErrors';
import { areaUnitForDistance } from '../../utils/serviceUnits';
import { asDistanceUnit, type AreaUnit } from '../../utils/units';
import {
  emptyWorkerForm,
  toWorkerSettingsInput,
  workerToForm,
  type ProviderWorker,
  type WorkerForm,
  type WorkerFormErrors,
  type WorkerFormKey,
} from './workerForm';

export interface SaveResult {
  ok: boolean;
  worker?: ProviderWorker;
  /** Set when the API refused the save; problems found here are in `errors` instead. */
  message?: string;
  error?: unknown;
}

/** A field the API named (`workingHours.monday`) on the form key that edits it. */
function formKeyOf(field: string): WorkerFormKey | null {
  const root = field.split('.')[0];
  if (root === 'workingHours') return 'workingHours';
  if (root === 'suppliesProvided') return 'supplies';
  const keys = Object.keys(emptyWorkerForm()) as WorkerFormKey[];
  return keys.includes(root as WorkerFormKey) ? (root as WorkerFormKey) : null;
}

/**
 * Loads a worker for its Provider and keeps the editable settings form.
 * `save(keys)` sends only those fields (plus `extra`), shows client-side
 * problems next to the boxes first, and refreshes the form from the answer.
 */
export function useWorkerEditor(carId?: string) {
  const query = useMyWorkerQuery({
    variables: { workerCarId: carId ?? '' },
    skip: !carId,
    fetchPolicy: 'cache-and-network',
  });
  const [updateWorker, { loading: saving }] = useUpdateWorkerMutation();
  const [form, setForm] = useState<WorkerForm>(emptyWorkerForm);
  const [errors, setErrors] = useState<WorkerFormErrors>({});
  const loadedFor = useRef<string | null>(null);
  const worker = query.data?.myWorker ?? null;

  useEffect(() => {
    if (!worker || loadedFor.current === worker.carId) return;
    loadedFor.current = worker.carId;
    setForm(workerToForm(worker));
  }, [worker]);

  const change = (patch: Partial<WorkerForm>) => {
    setForm(previous => ({ ...previous, ...patch }));
    setErrors(previous => {
      const next = { ...previous };
      Object.keys(patch).forEach(key => delete next[key as WorkerFormKey]);
      return next;
    });
  };

  const reset = () => {
    if (worker) setForm(workerToForm(worker));
    setErrors({});
  };

  const save = async (
    keys: readonly WorkerFormKey[],
    extra: Partial<IWorkerSettingsInput> = {},
  ): Promise<SaveResult> => {
    if (!carId) return { ok: false, message: 'Create the worker first' };
    const { input, errors: found } = toWorkerSettingsInput(form, keys);
    if (Object.keys(found).length) {
      setErrors(found);
      return { ok: false };
    }
    try {
      const { data } = await updateWorker({
        variables: { workerCarId: carId, input: { ...input, ...extra } },
      });
      const saved = data?.updateWorker;
      if (saved) setForm(workerToForm(saved));
      setErrors({});
      return { ok: true, worker: saved };
    } catch (error) {
      const fieldErrors: WorkerFormErrors = {};
      fieldProblemsOf(error).forEach(({ field, message }) => {
        const key = formKeyOf(field);
        if (key && !fieldErrors[key]) fieldErrors[key] = message;
      });
      setErrors(fieldErrors);
      return {
        ok: false,
        error,
        message: serviceErrorMessage(error, 'Could not save. Try again.'),
      };
    }
  };

  return {
    worker,
    loading: query.loading && !worker,
    loadError: query.error,
    refetch: query.refetch,
    form,
    errors,
    change,
    reset,
    save,
    saving,
  };
}

/** Spec 028: a worker in km prices homes in square metres (the same brand setting). */
function areaUnitOf(worker: ProviderWorker | null): AreaUnit {
  return areaUnitForDistance(
    asDistanceUnit((worker?.worker as { distanceUnit?: string | null } | undefined)?.distanceUnit),
  );
}

/**
 * The price forms of a worker's three services. `saveAll()` sends each
 * service that is offered, or was offered before (to turn it off), through
 * `upsertServiceOffering`; turned-on services go first, so a listed worker
 * never has zero services in between.
 */
export function useOfferingsEditor(worker: ProviderWorker | null) {
  const [forms, setForms] = useState<Record<ServiceType, OfferingForm>>(() =>
    offeringForms(worker?.worker.offerings ?? [], areaUnitOf(worker)),
  );
  const [errors, setErrors] = useState<Partial<Record<ServiceType, PriceErrors>>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [upsert, { loading: saving }] = useUpsertServiceOfferingMutation();
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!worker || loadedFor.current === worker.carId) return;
    loadedFor.current = worker.carId;
    setForms(offeringForms(worker.worker.offerings, areaUnitOf(worker)));
  }, [worker]);

  const change = (serviceType: ServiceType, next: OfferingForm) => {
    setForms(previous => ({ ...previous, [serviceType]: next }));
    setErrors(previous => ({ ...previous, [serviceType]: {} }));
    setMessage(null);
  };

  const reset = () => {
    if (worker) setForms(offeringForms(worker.worker.offerings, areaUnitOf(worker)));
    setErrors({});
    setMessage(null);
  };

  const saveAll = async (): Promise<SaveResult> => {
    if (!worker) return { ok: false, message: 'Create the worker first' };
    const found: Partial<Record<ServiceType, PriceErrors>> = {};
    const toSend = SERVICE_TYPES.map(type => forms[type])
      .filter(form => form.enabled || form.offeringId)
      .sort((a, b) => Number(b.enabled) - Number(a.enabled));
    const inputs = toSend.map(form => {
      const { input, errors: problems } = toOfferingInput(form);
      if (Object.keys(problems).length) found[form.serviceType] = problems;
      return input;
    });
    if (Object.keys(found).length) {
      setErrors(found);
      return { ok: false };
    }
    let latest: ProviderWorker | undefined;
    for (const input of inputs) {
      try {
        const { data } = await upsert({
          variables: { workerCarId: worker.carId, input: input as any },
        });
        latest = data?.upsertServiceOffering ?? latest;
      } catch (error) {
        const text = serviceErrorMessage(error, 'Could not save the prices. Try again.');
        setMessage(text);
        return { ok: false, error, message: text };
      }
    }
    if (latest) setForms(offeringForms(latest.worker.offerings));
    setErrors({});
    return { ok: true, worker: latest ?? worker };
  };

  return { forms, errors, message, change, reset, saveAll, saving };
}
